import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Divider,
  Tooltip,
  useMediaQuery,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import {
  AddRounded as AddIcon,
  EditRounded as EditIcon,
  DeleteRounded as DeleteIcon,
  PublishRounded as PublishIcon,
  VisibilityRounded as VisibilityIcon,
  StarRounded as StarIcon,
  StarBorderRounded as StarBorderIcon,
  ArticleRounded as ArticleIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import {
  getAllBlogPosts,
  deleteBlogPost,
  togglePublishStatus,
  toggleFeaturedStatus,
  getCategories,
} from '../../services/blogService';
import PageHeader from '../../components/ui/PageHeader';
import SearchField from '../../components/ui/SearchField';
import StatusChip from '../../components/ui/StatusChip';
import EmptyState from '../../components/ui/EmptyState';
import LoadingState from '../../components/ui/LoadingState';

const BlogPostsList = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // State for blog posts and pagination
  const [posts, setPosts] = useState([]);
  const [totalPosts, setTotalPosts] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // State for filtering and search. The text field updates `searchInput`
  // immediately; `filters.search` (which triggers fetches) follows after a
  // debounce so we don't fire a request per keystroke.
  const [searchInput, setSearchInput] = useState('');
  const [filters, setFilters] = useState({
    status: 'all',
    category: 'all',
    search: '',
  });
  const [categories, setCategories] = useState([]);

  // State for delete confirmation dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [postToDelete, setPostToDelete] = useState(null);

  const navigate = useNavigate();

  // Monotonic id so slow responses can't overwrite newer ones
  const fetchIdRef = useRef(0);

  // Fetch blog posts
  const fetchPosts = async () => {
    const fetchId = ++fetchIdRef.current;
    setLoading(true);
    try {
      const apiFilters = {};
      if (filters.status !== 'all') apiFilters.status = filters.status;
      if (filters.category !== 'all') apiFilters.category = filters.category;
      if (filters.search) apiFilters.search = filters.search;

      const response = await getAllBlogPosts(page + 1, rowsPerPage, apiFilters);
      if (fetchId !== fetchIdRef.current) return; // stale response, ignore
      setPosts(response.data);
      setTotalPosts(response.count);
    } catch (error) {
      if (fetchId !== fetchIdRef.current) return;
      console.error('Failed to fetch blog posts:', error);
    } finally {
      if (fetchId === fetchIdRef.current) {
        setLoading(false);
      }
    }
  };

  // Debounce search input into the active filters
  useEffect(() => {
    const timer = setTimeout(() => {
      setFilters((prev) => {
        if (prev.search === searchInput) return prev;
        return { ...prev, search: searchInput };
      });
      setPage(0);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // Fetch categories
  const fetchCategories = async () => {
    try {
      const response = await getCategories();
      setCategories(response.data || []);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  };

  // Load data on initial render and when dependencies change
  useEffect(() => {
    fetchPosts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, filters]);

  useEffect(() => {
    fetchCategories();
  }, []);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFilterChange = (event) => {
    const { name, value } = event.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
    setPage(0);
  };

  const handleSearchChange = (event) => {
    setSearchInput(event.target.value);
  };

  const handleDeleteClick = (post) => {
    setPostToDelete(post);
    setDeleteDialogOpen(true);
  };

  const handleDeleteClose = () => {
    setDeleteDialogOpen(false);
    setPostToDelete(null);
  };

  const handleDeleteConfirm = async () => {
    if (!postToDelete) return;

    try {
      await deleteBlogPost(postToDelete._id);
      fetchPosts(); // Refresh the list
      handleDeleteClose();
    } catch (error) {
      console.error('Failed to delete blog post:', error);
    }
  };

  const handleTogglePublish = async (id) => {
    try {
      await togglePublishStatus(id);
      fetchPosts(); // Refresh the list
    } catch (error) {
      console.error('Failed to toggle publish status:', error);
    }
  };

  const handleToggleFeatured = async (id) => {
    try {
      await toggleFeaturedStatus(id);
      fetchPosts(); // Refresh the list
    } catch (error) {
      console.error('Failed to toggle featured status:', error);
    }
  };

  const renderPostActions = (post, compact = false) => (
    <Stack direction="row" spacing={compact ? 0 : 0.25} justifyContent="flex-end">
      <Tooltip title={post.featured ? 'Remove from featured' : 'Add to featured'}>
        <IconButton
          size="small"
          onClick={() => handleToggleFeatured(post._id)}
          sx={{ color: post.featured ? 'warning.main' : 'text.disabled' }}
        >
          {post.featured ? <StarIcon fontSize="small" /> : <StarBorderIcon fontSize="small" />}
        </IconButton>
      </Tooltip>
      <Tooltip title={post.published ? 'Unpublish' : 'Publish'}>
        <IconButton
          size="small"
          onClick={() => handleTogglePublish(post._id)}
          sx={{ color: post.published ? 'success.main' : 'text.disabled' }}
        >
          <PublishIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title="Preview">
        <IconButton size="small" component={Link} to={`/blog/preview/${post._id}`}>
          <VisibilityIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title="Edit">
        <IconButton size="small" component={Link} to={`/blog/edit/${post._id}`}>
          <EditIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title="Delete">
        <IconButton size="small" sx={{ color: 'error.main' }} onClick={() => handleDeleteClick(post)}>
          <DeleteIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Stack>
  );

  return (
    <Box>
      <PageHeader
        title="Blog"
        subtitle="Write, publish, and feature posts on the Skyline site"
        actions={
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/blog/new')}>
            New Post
          </Button>
        }
      />

      {/* Filters & Search */}
      <Grid container spacing={1.5} alignItems="center" sx={{ mb: 2.5 }}>
        <Grid item xs={6} md={2.5}>
          <FormControl fullWidth size="small">
            <InputLabel id="status-filter-label">Status</InputLabel>
            <Select
              labelId="status-filter-label"
              id="status-filter"
              name="status"
              value={filters.status}
              label="Status"
              onChange={handleFilterChange}
            >
              <MenuItem value="all">All Statuses</MenuItem>
              <MenuItem value="published">Published</MenuItem>
              <MenuItem value="draft">Draft</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={6} md={2.5}>
          <FormControl fullWidth size="small">
            <InputLabel id="category-filter-label">Category</InputLabel>
            <Select
              labelId="category-filter-label"
              id="category-filter"
              name="category"
              value={filters.category}
              label="Category"
              onChange={handleFilterChange}
            >
              <MenuItem value="all">All Categories</MenuItem>
              {categories.map((category) => (
                <MenuItem key={category} value={category}>
                  {category}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={7}>
          <SearchField
            value={searchInput}
            onChange={handleSearchChange}
            placeholder="Search posts…"
            size="small"
          />
        </Grid>
      </Grid>

      {loading ? (
        <LoadingState label="Loading posts…" minHeight="40vh" />
      ) : posts.length === 0 ? (
        <Card>
          <EmptyState
            icon={<ArticleIcon />}
            title="No blog posts found"
            description={
              filters.search || filters.status !== 'all' || filters.category !== 'all'
                ? 'Try adjusting your filters or search.'
                : 'Write your first post to get started.'
            }
            action={
              <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/blog/new')}>
                New Post
              </Button>
            }
          />
        </Card>
      ) : isMobile ? (
        /* ------------------------- Mobile card list ------------------------- */
        <Stack spacing={1.5}>
          {posts.map((post) => (
            <Card key={post._id} sx={{ p: 2 }}>
              <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="subtitle2" sx={{ wordBreak: 'break-word' }}>
                    {post.title}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                    {post.category} · {post.updatedAt ? format(new Date(post.updatedAt), 'MMM d, yyyy') : '—'}
                  </Typography>
                </Box>
                <StatusChip status={post.published ? 'published' : 'draft'} />
              </Stack>
              <Divider sx={{ my: 1.25 }} />
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                {post.featured ? (
                  <StatusChip status="featured" color="warning" label="★ Featured" />
                ) : (
                  <Box />
                )}
                {renderPostActions(post, true)}
              </Stack>
            </Card>
          ))}
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={totalPosts}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage="Per page:"
          />
        </Stack>
      ) : (
        /* --------------------------- Desktop table -------------------------- */
        <Card>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Title</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell align="center">Status</TableCell>
                  <TableCell align="center">Featured</TableCell>
                  <TableCell>Last Updated</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {posts.map((post) => (
                  <TableRow key={post._id} hover>
                    <TableCell sx={{ maxWidth: 340 }}>
                      <Typography variant="body2" fontWeight={600} noWrap>
                        {post.title}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                        {post.slug}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{post.category}</Typography>
                    </TableCell>
                    <TableCell align="center">
                      <StatusChip status={post.published ? 'published' : 'draft'} />
                    </TableCell>
                    <TableCell align="center">
                      {post.featured ? (
                        <StatusChip status="featured" color="warning" label="★ Featured" />
                      ) : (
                        <Typography variant="body2" color="text.disabled">
                          —
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {post.updatedAt ? format(new Date(post.updatedAt), 'MMM d, yyyy') : '—'}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">{renderPostActions(post)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={totalPosts}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Card>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleDeleteClose} maxWidth="xs" fullWidth>
        <DialogTitle>Delete blog post?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            This will permanently remove the post and cannot be undone.
          </Typography>
          {postToDelete && (
            <Box
              sx={{
                p: 2,
                borderRadius: '12px',
                bgcolor: (t) => alpha(t.palette.error.main, 0.06),
                border: (t) => `1px solid ${alpha(t.palette.error.main, 0.2)}`,
              }}
            >
              <Typography variant="subtitle2">{postToDelete.title}</Typography>
              <Typography variant="body2" color="text.secondary">
                {postToDelete.category}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={handleDeleteClose} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            sx={{ backgroundImage: 'none' }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BlogPostsList;
